// TMOS Business Performance Service
// Date: January 13, 2026
// Purpose: Frontend service for business performance dashboard

import api from './api';
import {
  DashboardOverview,
  PlanHealthDetail,
  ObjectivePerformance,
  TargetVsActual,
  TrendDataPoint
} from '../types/businessPerformance';

export interface DashboardFilters {
  plan_type?: 'STRATEGIC' | 'ANNUAL' | 'QUARTERLY';
  plan_year?: number;
  business_hierarchy_id?: string;
  functional_hierarchy_id?: string;
  geographic_hierarchy_id?: string;
}

export interface TargetVsActualFilters {
  plan_id?: string;
  kpi_id?: string;
  business_hierarchy_id?: string;
  functional_hierarchy_id?: string;
  geographic_hierarchy_id?: string;
}

export const businessPerformanceService = {
  /**
   * Get executive dashboard overview
   */
  async getDashboardOverview(filters?: DashboardFilters): Promise<DashboardOverview> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const response = await api.get<{ success: boolean; data: DashboardOverview }>(
      `/business-performance/dashboard/overview${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data.data;
  },

  /**
   * Get plan health detail
   */
  async getPlanHealthDetail(planId: string): Promise<PlanHealthDetail> {
    const response = await api.get<{ success: boolean; data: PlanHealthDetail }>(
      `/business-performance/dashboard/plans/${planId}/health`
    );
    return response.data.data;
  },

  /**
   * Get objective performance
   */
  async getObjectivePerformance(objectiveId: string, planId?: string): Promise<ObjectivePerformance> {
    const params = planId ? `?plan_id=${planId}` : '';
    const response = await api.get<{ success: boolean; data: ObjectivePerformance }>(
      `/business-performance/dashboard/objectives/${objectiveId}/performance${params}`
    );
    return response.data.data;
  },

  /**
   * Get target vs actual report
   */
  async getTargetVsActual(filters?: TargetVsActualFilters): Promise<TargetVsActual[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const response = await api.get<{ success: boolean; data: TargetVsActual[]; total: number }>(
      `/business-performance/dashboard/target-vs-actual${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data.data;
  },

  /**
   * Get historical trends
   */
  async getHistoricalTrends(objectiveId: string, planId?: string, months: number = 12): Promise<TrendDataPoint[]> {
    const params = new URLSearchParams();
    if (planId) params.append('plan_id', planId);
    params.append('months', String(months));
    const response = await api.get<{ success: boolean; data: TrendDataPoint[] }>(
      `/business-performance/dashboard/trends/${objectiveId}?${params.toString()}`
    );
    return response.data.data;
  }
};

